import { ProductComplete, BundleDetails } from '../types/models';
import merge from 'deepmerge';

type ProductObjectValues = string | object | number | null;

const findProductByVariantId = (
  products: ProductComplete[],
  variantId: number,
): ProductComplete | undefined => {
  return products.find(({ attributes }) => attributes.variantId === variantId);
};

const getMergeOptions = () => ({
  arrayMerge: (target, source) => source,
  customMerge: (key) => {
    if (key === 'attributes') {
      return (target, source) => ({
        ...target,
        ...source,
      });
    }
    return undefined;
  },
});
const buildVariants = (
  baseProduct: ProductComplete,
  completeBaseProducts: ProductComplete[],
  completeBundleProducts: ProductComplete[],
): ProductComplete[] => {
  const variantIds = baseProduct.attributes?.variantDetails?.ids || [];
  return variantIds.reduce((accum: ProductComplete[], id: number) => {
    const completeBundleProduct = findProductByVariantId(completeBundleProducts, id);
    if (completeBundleProduct) {
      const merged = merge(
        baseProduct as Partial<ProductComplete>,
        completeBundleProduct,
        getMergeOptions(),
      );
      accum.push(merged);
    }
    const completeProduct = findProductByVariantId(completeBaseProducts, id);
    if (completeProduct) {
      const merged = merge(
        baseProduct as Partial<ProductComplete>,
        completeProduct,
        getMergeOptions(),
      );
      accum.push(merged);
    }
    return accum;
  }, []);
};

const buildBundle = (
  baseProduct: ProductComplete,
  completeBaseProducts: ProductComplete[],
): BundleDetails | null => {
  const bundleDetails = baseProduct.attributes?.bundleDetails;
  if (!bundleDetails || !completeBaseProducts.length) return null;
  const {
    title,
    bundleType,
    pricingRules,
    bundleItems,
    additionalBundleDetails = null,
    minItems = null,
    maxItems = null,
  } = bundleDetails;
  const processedItems = bundleItems.map((bundleGroup) => {
    const processedProducts = bundleGroup.productIds
      .map((variantId) => {
        return findProductByVariantId(completeBaseProducts, variantId);
      })
      .filter(Boolean);
    return { ...bundleGroup, processedProducts };
  });

  return {
    title,
    bundleType,
    pricingRules,
    bundleItems: processedItems,
    ...(additionalBundleDetails && { additionalBundleDetails }),
    ...(minItems && { minItems }),
    ...(maxItems && { maxItems }),
  };
};

export const buildCompleteProduct = (
  baseProduct: ProductComplete,
  completeBaseProducts: ProductComplete[],
  completeBundleProducts: ProductComplete[],
): ProductComplete => {
  const variantProducts = buildVariants(baseProduct, completeBaseProducts, completeBundleProducts);
  const bundleDefinition = buildBundle(baseProduct, completeBaseProducts);
  return {
    ...baseProduct,
    ...(variantProducts.length > 0 && { variantProducts }),
    ...(bundleDefinition && { bundleDefinition }),
  };
};

export const getProductValueByPath = (
  productObj: ProductComplete,
  path: string,
): ProductObjectValues | undefined => {
  const foundValue = path.split('.').reduce((acc, key) => {
    return acc && acc[key];
  }, productObj);
  return foundValue;
};
